import React from 'react';
import {
  FaArrowRight, FaCalendarAlt, FaQuoteLeft, FaQuoteRight, FaBuilding,
  FaEye, FaBullseye, FaBell, FaPlaneDeparture, FaHotel, FaPassport,
  FaHeart, FaUsers, FaComments, FaMapMarkedAlt, FaFileSignature,
  FaCreditCard, FaSuitcaseRolling, FaHeadset, FaLinkedinIn, FaEnvelope,
  FaStar, FaShieldAlt, FaUserFriends,
} from 'react-icons/fa';
import { useNavigate }    from 'react-router-dom';

const AboutUs: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-white text-brand-dark w-full min-h-screen m-0 p-0 overflow-x-hidden">
      {/* 1. Hero Section */}
      <section id="hero" className="relative pt-20 h-[80vh] min-h-[650px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            className="w-full h-full object-cover"
            src="https://storage.googleapis.com/uxpilot-auth.appspot.com/90cfda62b1-40a2a71922205716eb12.png"
            alt="luxury overwater bungalow in maldives at sunset"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block py-1 px-3 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold tracking-wider uppercase mb-6">
              Premium Travel Division
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white leading-tight mb-6">
              Crafting Your <br />
              <span className="text-brand-pink relative">
                Perfect Escape
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-brand-pink opacity-50" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 font-light leading-relaxed max-w-2xl">
              Seamless, customized, and high-quality travel experiences global-wide. We transform your travel dreams into meticulously curated realities.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button onClick={() => navigate('/destination')} className="px-8 py-4 rounded-full bg-brand-pink text-white font-medium text-center hover:bg-white hover:text-brand-pink transition-all duration-300 shadow-lg shadow-brand-pink/30 flex items-center justify-center gap-2 group">
                Explore Destinations
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => navigate('/help-and-support')} className="px-8 py-4 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white font-medium text-center hover:bg-white hover:text-brand-dark transition-all duration-300 flex items-center justify-center gap-2">
                <FaCalendarAlt />
                Book Consultation
              </button>
            </div>

            <div className="flex items-center gap-8 md:gap-16 border-t border-white/20 pt-8">
              <div>
                <div className="text-3xl font-serif font-bold text-white mb-1">10+</div>
                <div className="text-sm text-gray-300 uppercase tracking-wider">Years Experience</div>
              </div>
              <div>
                <div className="text-3xl font-serif font-bold text-white mb-1">50+</div>
                <div className="text-sm text-gray-300 uppercase tracking-wider">Global Partners</div>
              </div>
              <div className="hidden sm:block">
                <div className="text-3xl font-serif font-bold text-white mb-1">24/7</div>
                <div className="text-sm text-gray-300 uppercase tracking-wider">Concierge Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Company Overview */}
      <section id="overview" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-brand-pink/10 rounded-full z-0"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-brand-pink/20 rounded-full z-0"></div>
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <div className="space-y-4 pt-8">
                  <div className="h-64 rounded-2xl overflow-hidden shadow-elegant">
                    <img className="w-full h-full object-cover" src="https://storage.googleapis.com/uxpilot-auth.appspot.com/dcb2aece7f-7845b6a52a0d39b29421.png" alt="luxury hotel lobby" />
                  </div>
                  <div className="h-48 rounded-2xl overflow-hidden shadow-elegant">
                    <img className="w-full h-full object-cover" src="https://storage.googleapis.com/uxpilot-auth.appspot.com/87d9d58792-c651475bab6afe67c1e4.png" alt="first class airplane seat" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="h-48 rounded-2xl overflow-hidden shadow-elegant">
                    <img className="w-full h-full object-cover" src="https://storage.googleapis.com/uxpilot-auth.appspot.com/dd419a3f42-8cb63895621ccea5e13d.png" alt="couple on beach" />
                  </div>
                  <div className="h-64 rounded-2xl overflow-hidden shadow-elegant bg-brand-pink p-8 flex flex-col justify-center text-white">
                    <FaQuoteLeft className="text-4xl mb-4 opacity-50" />
                    <p className="text-lg font-serif italic mb-4">"Travel is the only thing you buy that makes you richer."</p>
                    <p className="text-sm font-semibold uppercase tracking-wider opacity-80">- Get2Vacations</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-pink/10 text-brand-pink text-sm font-medium mb-6">
                <FaBuilding />
                <span>Company Overview</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-dark mb-6 leading-tight">
                Elevating the Art of <span className="text-brand-pink">Travel</span>
              </h2>

              <div className="prose prose-lg text-brand-muted mb-8">
                <p className="font-medium text-brand-dark">
                  Founded in 2026 by Mr. KV Hidayath and Ms. Amina Hidayath, Get2Vacations emerged as the premium holidays division of Getfares Travels.
                </p>
                <p>
                  Our story began with a simple realization: modern travelers seek more than just bookings; they desire transformative experiences. Evolving from our parent company's robust foundation in global ticketing, we established Get2Vacations to focus exclusively on curated, high-end leisure and corporate travel.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
                <div className="p-6 rounded-2xl border border-gray-100 hover:border-brand-pink/30 hover:shadow-card transition-all group bg-white">
                  <div className="w-12 h-12 rounded-full bg-brand-pink/10 text-brand-pink flex items-center justify-center text-xl mb-4 group-hover:bg-brand-pink group-hover:text-white transition-colors">
                    <FaEye />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Our Vision</h3>
                  <p className="text-brand-muted text-sm">To be the world's most trusted partner in crafting bespoke, unforgettable journeys.</p>
                </div>
                <div className="p-6 rounded-2xl border border-gray-100 hover:border-brand-pink/30 hover:shadow-card transition-all group bg-white">
                  <div className="w-12 h-12 rounded-full bg-brand-pink/10 text-brand-pink flex items-center justify-center text-xl mb-4 group-hover:bg-brand-pink group-hover:text-white transition-colors">
                    <FaBullseye />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Our Mission</h3>
                  <p className="text-brand-muted text-sm">Delivering seamless, personalized travel experiences through innovation and global expertise.</p>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-gray-100">
                <h4 className="text-sm font-bold uppercase tracking-wider text-brand-dark mb-4">Core Values</h4>
                <div className="flex flex-wrap gap-3">
                  {["Customer First", "Transparency", "Quality", "Innovation"].map((v) => (
                    <span key={v} className="px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-brand-dark hover:border-brand-pink hover:text-brand-pink transition-colors cursor-default">{v}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Services */}
      <section id="services" className="py-24 bg-brand-gray/30 relative">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-brand-pink/5 to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-brand-pink/20 text-brand-pink text-sm font-medium mb-4 shadow-sm">
              <FaBell />
              <span>Premium Services</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-dark mb-6">
              Comprehensive <span className="text-brand-pink">Travel Solutions</span>
            </h2>
            <p className="text-lg text-brand-muted">
              From the moment of inspiration to your safe return, our suite of services covers every aspect of your journey with uncompromising quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <FaPlaneDeparture />, title: "Premium Flights", desc: "Access to global airline networks with priority booking, preferred seating, and competitive business/first-class fares." },
              { icon: <FaHotel />, title: "Luxury Hotels", desc: "Curated selection of 5-star properties, boutique resorts, and private villas with exclusive amenities and upgrades." },
              { icon: <FaPassport />, title: "Worldwide Visas", desc: "Hassle-free visa processing with expert guidance, documentation support, and expedited services for global destinations." },
              { icon: <FaHeart />, title: "Honeymoon & Luxury", desc: "Bespoke romantic getaways and ultra-luxury escapes designed with meticulous attention to intimate details." },
              { icon: <FaUserFriends />, title: "Corporate MICE", desc: "End-to-end management for Meetings, Incentives, Conferences, and Exhibitions with flawless execution." },
              { icon: <FaShieldAlt />, title: "Travel Insurance", desc: "Comprehensive coverage options ensuring peace of mind against medical emergencies, cancellations, and delays." },
            ].map((service) => (
              <div key={service.title} className="bg-white rounded-3xl p-8 shadow-card hover:-translate-y-2 transition-transform duration-300 border border-gray-50 group">
                <div className="w-16 h-16 rounded-2xl bg-brand-gray flex items-center justify-center text-brand-pink text-2xl mb-6 group-hover:bg-brand-pink group-hover:text-white transition-colors duration-300 shadow-sm">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-serif font-bold mb-3 text-brand-dark">{service.title}</h3>
                <p className="text-brand-muted mb-6 leading-relaxed">{service.desc}</p>
                {/* <button onClick={() => navigate('/destination')} className="inline-flex items-center text-brand-pink font-medium hover:text-brand-dark transition-colors gap-2">
                  Learn more <FaArrowRight className="text-sm" />
                </button> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Team Section */}
      <section id="team" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-pink/10 text-brand-pink text-sm font-medium mb-6">
                <FaUsers />
                <span>Leadership</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-dark mb-6">
                Guided by <span className="text-brand-pink">Expertise</span>
              </h2>
              <p className="text-lg text-brand-muted mb-8">
                Behind every seamless journey is a team of dedicated professionals who understand the nuances of luxury travel.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[
                  {
                    name: "K.V Hidayathulla",
                    role: "President & Director",
                    note: "Leading Akbar Group since 1990, establishing a nationwide presence with unwavering commitment to excellence and innovation in travel.",
                    image: "/hid.png",
                  },
                  {
                    name: "K.V Abdul Nazar",
                    role: "Founder & Managing Director",
                    note: "Akbar Group founder, renowned for achieving extraordinary milestones and transforming the travel landscape across India.",
                    image: "/abdul.jpeg",
                  },
                  {
                    name: "Ms. Amina Hidayath",
                    role: "Managing Director, GETFARES-UAE",
                    note: "From an airline family and armed with an MBA in Aviation, she launched GETFARES in Dubai in 2024 and has rapidly built a strong UAE presence through deep industry knowledge, customer focus, and ambitious leadership.",
                    image: "/AminaHidayath.JPG",
                    imagePosition: "42% 18%",
                  },
                ].map((member) => (
                  <div key={member.name} className="bg-white rounded-3xl p-8 shadow-elegant border border-gray-100 relative overflow-hidden h-full">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-brand-pink/5 rounded-full -mr-10 -mt-10"></div>
                    <div className="relative z-10 flex h-full flex-col sm:flex-row gap-6 items-start">
                      <div className="w-24 h-32 rounded-2xl overflow-hidden shrink-0 border-4 border-white shadow-md mx-auto sm:mx-0">
                        {member.image ? (
                          <img
                            className="w-full h-full object-cover"
                            src={member.image}
                            alt={member.name}
                            style={member.imagePosition ? { objectPosition: member.imagePosition } : undefined}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-brand-pink/15 via-white to-brand-dark/10 flex items-center justify-center text-center px-3">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-dark/70">
                              Photo Soon
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col self-stretch text-center sm:text-left">
                        <h3 className="text-xl font-serif font-bold text-brand-dark mb-1">{member.name}</h3>
                        <p className="text-brand-pink font-medium mb-3 text-sm">{member.role}</p>
                        <p className="text-brand-muted text-sm leading-relaxed mb-4">{member.note}</p>
                        <div className="flex gap-3 mt-auto justify-center sm:justify-start">
                          <a href="#" className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-brand-dark hover:bg-brand-pink hover:text-white transition-colors">
                            <FaLinkedinIn className="text-sm" />
                          </a>
                          <a href="#" className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-brand-dark hover:bg-brand-pink hover:text-white transition-colors">
                            <FaEnvelope className="text-sm" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>
        </div>
      </section>

      {/* 5. Work Approach */}
      <section id="approach" className="py-24 bg-brand-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(rgb(239, 0, 101) 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
              Our <span className="text-brand-pink">Approach</span>
            </h2>
            <p className="text-lg text-gray-400">A transparent, personalized workflow designed to make planning as enjoyable as the journey itself.</p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-[30%] left-20 w-[85%] h-0.5 bg-gray-800 -translate-y-1/2 z-0"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-6 lg:gap-4 relative z-10">
              {[
                { icon: <FaComments />, label: "Consultation", sub: "Understanding your vision", active: false },
                { icon: <FaMapMarkedAlt />, label: "Planning", sub: "Curating the details", active: false },
                { icon: <FaFileSignature />, label: "Proposal", sub: "Transparent pricing", active: false },
                { icon: <FaCreditCard />, label: "Booking", sub: "Securing reservations", active: false },
                { icon: <FaPassport />, label: "Visa", sub: "Documentation prep", active: false },
                { icon: <FaSuitcaseRolling />, label: "Pre-departure", sub: "Final briefings", active: false },
                { icon: <FaHeadset />, label: "On-trip", sub: "24/7 dedicated support", active: false },
              ].map((step, i) => (
                <div key={step.label} className="flex flex-col items-center text-center group">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl mb-4 relative z-10 transition-all duration-300 ${
                    step.active
                      ? "bg-gray-900 border-2 border-brand-pink text-brand-pink "
                      : "bg-gray-900 border-2 border-gray-700 text-gray-400 group-hover:border-brand-pink group-hover:shadow-[0_0_15px_rgba(239,0,101,0.5)] group-hover:text-brand-pink"
                  }`}>
                    {step.icon}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-brand-pink text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                  </div>
                  <h4 className={`font-bold mb-2 ${step.active ? "text-brand-pink" : ""}`}>{step.label}</h4>
                  <p className="text-xs text-gray-400">{step.sub}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-20 bg-gray-900 rounded-3xl p-8 md:p-12 border border-gray-800 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h3 className="text-2xl font-serif font-bold mb-4">Ready to start planning?</h3>
              <p className="text-gray-400">Experience the difference of true personalization and absolute transparency.</p>
            </div>
            <button
              onClick={() => navigate('/help-and-support')}
              className="px-8 py-4 rounded-full bg-brand-pink text-white font-medium hover:bg-white hover:text-brand-dark transition-all duration-300 whitespace-nowrap shadow-lg shadow-brand-pink/20"
            >
              Book a Consultation
            </button>
          </div>
        </div>
      </section>

      {/* 6. Testimonials */}
      <section id="testimonials" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-dark mb-6">
              Client <span className="text-brand-pink">Experiences</span>
            </h2>
            <p className="text-lg text-brand-muted">Don't just take our word for it. Hear from travelers who have experienced the Get2Vacations difference.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-3xl bg-gray-50 border border-gray-100 relative">
              <FaQuoteRight className="absolute top-8 right-8 text-4xl text-brand-pink/10" />
              <div className="flex text-brand-pink text-sm mb-4 gap-1">
                {[...Array(5)].map((_, i) => <FaStar key={i} />)}
              </div>
              <p className="text-brand-dark italic mb-6">"Flawless execution of our corporate retreat. The attention to detail and VIP treatment at every touchpoint was remarkable."</p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-300 overflow-hidden">
                  <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg" alt="Client" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h5 className="font-bold text-sm">James Wilson</h5>
                  <p className="text-xs text-brand-muted">Corporate Client</p>
                </div>
              </div>
            </div>

            <div className="p-8 rounded-3xl bg-brand-pink text-white shadow-lg shadow-brand-pink/20 relative transform md:-translate-y-4">
              <FaQuoteRight className="absolute top-8 right-8 text-4xl text-white/10" />
              <div className="flex text-yellow-300 text-sm mb-4 gap-1">
                {[...Array(5)].map((_, i) => <FaStar key={i} />)}
              </div>
              <p className="text-white italic mb-6">"Our Maldives honeymoon was out of this world. Suhail and the team handled everything. We just had to show up and enjoy."</p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 overflow-hidden border border-white/50">
                  <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg" alt="Client" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h5 className="font-bold text-sm">Sarah & Mark</h5>
                  <p className="text-xs text-white/80">Honeymooners</p>
                </div>
              </div>
            </div>

            <div className="p-8 rounded-3xl bg-gray-50 border border-gray-100 relative">
              <FaQuoteRight className="absolute top-8 right-8 text-4xl text-brand-pink/10" />
              <div className="flex text-brand-pink text-sm mb-4 gap-1">
                {[...Array(5)].map((_, i) => <FaStar key={i} />)}
              </div>
              <p className="text-brand-dark italic mb-6">"The visa process has always stressed me out, but their team made it completely seamless. Highly recommend their services."</p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-300 overflow-hidden">
                  <img src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-8.jpg" alt="Client" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h5 className="font-bold text-sm">David Chen</h5>
                  <p className="text-xs text-brand-muted">Frequent Traveler</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
